import { ApolloLink, FetchResult, NextLink, Observable } from "@apollo/client"
import { getMainDefinition } from "@apollo/client/utilities"
import { DocumentNode } from "graphql"

/** Keep in sync with REDIRECT_EXTENSION_KEY in the GraphQL server errors module. */
export const REDIRECT_EXTENSION_KEY = "redirectTo"

const isSafeRelativeRedirect = (value: unknown): value is string =>
  typeof value === "string" && value.startsWith("/") && !value.startsWith("//")

const isMutationOperation = (query: DocumentNode) => {
  const definition = getMainDefinition(query)
  // Compare string literals so dual graphql package copies cannot break ===.
  return (
    definition.kind === "OperationDefinition" &&
    "operation" in definition &&
    definition.operation === "mutation"
  )
}

const getRedirectTo = (result: FetchResult): string | null => {
  const errors = result.errors
  if (!errors?.length) return null
  for (const error of errors) {
    const redirectTo = error.extensions?.[REDIRECT_EXTENSION_KEY]
    if (isSafeRelativeRedirect(redirectTo)) return redirectTo
  }
  return null
}

export type CreateRedirectLinkOptions = {
  navigate?: (path: string) => void
  getCurrentPathname?: () => string
}

/**
 * Follows GraphQL mutation errors that carry a relative `redirectTo` extension
 * (e.g. Django middleware sending archived members to /home/).
 */
export const createRedirectLink = ({
  navigate = (path) => {
    window.location.assign(path)
  },
  getCurrentPathname = () => window.location.pathname
}: CreateRedirectLinkOptions = {}) =>
  new ApolloLink((operation, forward: NextLink) => {
    return new Observable((observer) => {
      const subscription = forward(operation).subscribe({
        next: (result) => {
          if (isMutationOperation(operation.query)) {
            const redirectTo = getRedirectTo(result)
            if (redirectTo) {
              try {
                const targetPathname = new URL(
                  redirectTo,
                  "https://placeholder.invalid"
                ).pathname
                if (getCurrentPathname() !== targetPathname) {
                  navigate(redirectTo)
                  // Swallow the result — the page is navigating away.
                  return
                }
              } catch {
                // Fall through and emit the result.
              }
            }
          }
          observer.next(result)
        },
        error: (error) => observer.error(error),
        complete: () => observer.complete()
      })
      return () => subscription.unsubscribe()
    })
  })
